import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  Popover,
  CircularProgress,
  Collapse,
  Button,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  DownOutlined,
  RightOutlined,
  ApartmentOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useSelector, dispatch } from 'store';
import { updateSelectedCompany } from 'store/reducers/dashboardSearch';
import { fetchCompanyDetails } from 'store/reducers/companyTabDetails';
import authApi from 'interceptor/authInterceptor';

export interface CompanyHierarchyNode {
  _id: string;
  company_name: string;
  parentEntityId?: string | null;
  inheritParentSettings?: boolean;
  abn?: string;
  currency?: string;
  children?: CompanyHierarchyNode[];
}

/**
 * Builds N-level tree from flat array of companies using parentEntityId
 */
export const buildHierarchyTree = (
  companies: any[],
  isSuperAdmin: boolean,
  userCompanyId: string
): CompanyHierarchyNode[] => {
  if (!companies || !Array.isArray(companies) || companies.length === 0) {
    return [];
  }

  // Normalize id & parentEntityId to strings
  const nodeMap = new Map<string, CompanyHierarchyNode>();
  companies.forEach((comp) => {
    if (!comp || !comp._id) return;
    const id = typeof comp._id === 'object' ? (comp._id.$oid || String(comp._id)) : String(comp._id);
    const parentId = comp.parentEntityId
      ? typeof comp.parentEntityId === 'object'
        ? comp.parentEntityId.$oid || String(comp.parentEntityId)
        : String(comp.parentEntityId)
      : null;

    nodeMap.set(id, {
      _id: id,
      company_name: comp.company_name || 'Unnamed Company',
      parentEntityId: parentId,
      inheritParentSettings: comp.inheritParentSettings !== false,
      abn: comp.abn || comp.businessNumber || '',
      currency: comp.currency || 'AUD',
      children: []
    });
  });

  const roots: CompanyHierarchyNode[] = [];
  nodeMap.forEach((node) => {
    if (node.parentEntityId && nodeMap.has(node.parentEntityId)) {
      const parent = nodeMap.get(node.parentEntityId)!;
      parent.children = parent.children || [];
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const isDescendant = (parent: CompanyHierarchyNode, targetId: string): boolean => {
  if (parent._id === targetId) return true;
  if (!parent.children) return false;
  return parent.children.some((child) => isDescendant(child, targetId));
};

interface TreeItemProps {
  node: CompanyHierarchyNode;
  level: number;
  selectedNodes: { [id: string]: boolean };
  currentActiveId: string;
  onSelectRoot: (node: CompanyHierarchyNode) => void;
  onToggleCheck: (node: CompanyHierarchyNode, checked: boolean) => void;
}

const TreeItem: React.FC<TreeItemProps> = ({
  node,
  level,
  selectedNodes,
  currentActiveId,
  onSelectRoot,
  onToggleCheck
}) => {
  const hasChildren = Boolean(node.children && node.children.length > 0);
  const [expanded, setExpanded] = useState<boolean>(true);
  const isChecked = selectedNodes[node._id] !== false;
  const isCurrentActive = currentActiveId === node._id;

  const handleCheckbox = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleCheck(node, !isChecked);
  };

  const handleRowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectRoot(node);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        onClick={handleRowClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: level * 2.8 + 1,
          pr: 1.5,
          py: 0.6,
          cursor: 'pointer',
          borderRadius: 1,
          bgcolor: isCurrentActive ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: isCurrentActive ? 'action.selected' : 'action.hover'
          }
        }}
      >
        <Checkbox
          size="small"
          checked={isChecked}
          onClick={handleCheckbox}
          sx={{ p: 0.4, mr: 1 }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: isCurrentActive ? 600 : level === 0 ? 600 : 400,
              fontSize: '13px',
              color: isCurrentActive ? 'primary.main' : 'text.primary'
            }}
          >
            {node.company_name}
          </Typography>
          {node.abn ? (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '11px' }}>
              ABN: {node.abn}
            </Typography>
          ) : null}
        </Box>

        {node.parentEntityId && (
          <Typography
            variant="caption"
            sx={{
              mx: 1,
              px: 0.8,
              py: 0.2,
              borderRadius: 0.8,
              fontSize: '10px',
              bgcolor: isChecked ? 'success.lighter' : 'grey.200',
              color: isChecked ? 'success.dark' : 'text.disabled'
            }}
          >
            {isChecked ? 'Inherited' : 'Custom'}
          </Typography>
        )}

        {/* Dropdown arrow on the far right after company name & badge */}
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{ p: 0.4, ml: 0.5 }}
          >
            {expanded ? (
              <DownOutlined style={{ fontSize: '10px', color: '#1890ff' }} />
            ) : (
              <RightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
            )}
          </IconButton>
        ) : null}
      </Box>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {node.children!.map((child) => (
            <TreeItem
              key={child._id}
              node={child}
              level={level + 1}
              selectedNodes={selectedNodes}
              currentActiveId={currentActiveId}
              onSelectRoot={onSelectRoot}
              onToggleCheck={onToggleCheck}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

export const CompanyHierarchySelector: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [rawCompanies, setRawCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedNodes, setSelectedNodes] = useState<{ [id: string]: boolean }>({});
  const [searchFilter, setSearchFilter] = useState<string>('');

  const { selectedCompany } = useSelector((state: any) => state.dashboardSearch);
  const { onBoardedCompanyDetails, isCashDSuperAdmin } = useSelector((state: any) => state.auth);
  const userPrimaryCompanyId = onBoardedCompanyDetails?._id ? String(onBoardedCompanyDetails._id) : '';

  // Fetch companies from backend
  const loadCompanies = async () => {
    try {
      setLoading(true);
      // Fetch from /v2/api/companies/listCompanies (role-filtered by backend)
      const res = await authApi.get('/v2/api/companies/listCompanies');
      if (res?.data?.data && Array.isArray(res.data.data)) {
        setRawCompanies(res.data.data);
      } else {
        // Fallback to multipleCompanies
        const res2 = await authApi.get('/v2/api/users/multipleCompanies');
        if (res2?.data?.data && Array.isArray(res2.data.data)) {
          setRawCompanies(res2.data.data);
        }
      }
    } catch (err) {
      console.error('Failed to load companies for hierarchy selector:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  // Build the hierarchical tree from the company list strictly scoped to user role & primary company
  const hierarchyTree = useMemo(() => {
    return buildHierarchyTree(rawCompanies, Boolean(isCashDSuperAdmin), userPrimaryCompanyId);
  }, [rawCompanies, isCashDSuperAdmin, userPrimaryCompanyId]);

  // Default all nodes to checked if inheritParentSettings !== false
  useEffect(() => {
    if (rawCompanies.length > 0) {
      const initialMap: { [id: string]: boolean } = {};
      rawCompanies.forEach((comp) => {
        const id = typeof comp._id === 'object' ? (comp._id.$oid || String(comp._id)) : String(comp._id);
        initialMap[id] = comp.inheritParentSettings !== false;
      });
      setSelectedNodes(initialMap);
    }
  }, [rawCompanies]);


  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleSelectRoot = (node: CompanyHierarchyNode) => {
    // Collect all descendant ids of this node
    const collectDescendantIds = (item: CompanyHierarchyNode, acc: string[] = []): string[] => {
      acc.push(item._id);
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach(child => collectDescendantIds(child, acc));
      }
      return acc;
    };
    const nodeTreeIds = collectDescendantIds(node);

    // Filter only those that are currently checked (or default to the node itself)
    const activeSubtreeChecked = nodeTreeIds.filter(id => selectedNodes[id] !== false);
    const finalIds = activeSubtreeChecked.length > 0 ? activeSubtreeChecked : [node._id];

    dispatch(updateSelectedCompany({
      selectedCompany: {
        _id: node._id,
        company_name: node.company_name,
        abn: node.abn,
        currency: node.currency
      },
      selectedCompanyIds: finalIds
    }));
    // dispatch(updateSelectedCompanyIds(finalIds));
    dispatch(fetchCompanyDetails(node._id));
  };

  const handleToggleCheck = (node: CompanyHierarchyNode, checked: boolean) => {
    setSelectedNodes((prev) => {
      const updated = { ...prev, [node._id]: checked };

      // Cascade to all descendants of this node
      const cascadeDescendants = (item: CompanyHierarchyNode) => {
        if (item.children && Array.isArray(item.children)) {
          item.children.forEach((child) => {
            updated[child._id] = checked;
            cascadeDescendants(child);
          });
        }
      };
      cascadeDescendants(node);

      return updated;
    });
  };

  // Filter tree by search term if typed
  const filteredTree = useMemo(() => {
    if (!searchFilter.trim()) return hierarchyTree;
    const term = searchFilter.toLowerCase();

    const filterNode = (node: CompanyHierarchyNode): CompanyHierarchyNode | null => {
      const matches = node.company_name.toLowerCase().includes(term);
      const filteredChildren = (node.children || [])
        .map(filterNode)
        .filter((child): child is CompanyHierarchyNode => child !== null);

      if (matches || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren
        };
      }
      return null;
    };

    return hierarchyTree
      .map(filterNode)
      .filter((root): root is CompanyHierarchyNode => root !== null);
  }, [hierarchyTree, searchFilter]);

  const displayName =
    selectedCompany?.company_name ||
    onBoardedCompanyDetails?.company_name ||
    'Select Company';

  return (
    <Box sx={{ position: 'relative', minWidth: 260, maxWidth: 380 }}>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.8,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: open ? 'primary.main' : 'divider',
          bgcolor: 'background.paper',
          cursor: 'pointer',
          boxShadow: open ? '0 0 0 2px rgba(24, 144, 255, 0.2)' : 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, mr: 1 }}>
          <ApartmentOutlined style={{ marginRight: 8, fontSize: '15px', color: '#1890ff' }} />
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ fontWeight: 600, fontSize: '13.5px', color: 'text.primary' }}
          >
            {displayName}
          </Typography>
        </Box>
        {loading ? (
          <CircularProgress size={16} />
        ) : (
          <DownOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: 380,
            maxHeight: 500,
            p: 1.5,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
          }
        }}
      >
        <Box sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
              Company Hierarchy Tree
            </Typography>
            <Button
              size="small"
              onClick={() => {
                const allChecked: { [id: string]: boolean } = {};
                rawCompanies.forEach((c) => {
                  const id = typeof c._id === 'object' ? (c._id.$oid || String(c._id)) : String(c._id);
                  allChecked[id] = true;
                });
                setSelectedNodes(allChecked);
                // dispatch(updateSelectedCompanyIds(Object.keys(allChecked)));
              }}
              sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
            >
              Select All
            </Button>
          </Box>

          <TextField
            size="small"
            fullWidth
            placeholder="Search companies..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined style={{ color: '#8c8c8c' }} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Box sx={{ py: 1, overflowY: 'auto', maxHeight: 360 }}>
          {filteredTree.length > 0 ? (
            filteredTree.map((rootNode) => (
              <TreeItem
                key={rootNode._id}
                node={rootNode}
                level={0}
                selectedNodes={selectedNodes}
                currentActiveId={selectedCompany?._id || ''}
                onSelectRoot={handleSelectRoot}
                onToggleCheck={handleToggleCheck}
              />
            ))
          ) : (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {loading ? 'Loading hierarchy...' : 'No companies found'}
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default CompanyHierarchySelector;
